import { AnalyticsExportButton } from './AnalyticsHeaderSlot';
import { Box, Checkbox, Grid, Toolbar, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import moment from 'moment-timezone';
import React from 'react';

import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';
import TimezonePicker from '../components/TimezonePicker';
import { useIsWidthUp } from '../utils';
import { DataAPIContext, GET_LIST } from '../api';
import { ContextStore } from '../store/contextStore';
import FormControlLabel from '@mui/material/FormControlLabel';
import { BY_DAY_MAX_DAYS } from './agentMetricsByDay';

// CP-6848: Autocomplete client-filters by getOptionLabel. Agent options are
// labeled with display_name, so an email query the backend already matched
// is dropped to "No matches". Keep the remote result set as-is.
export const filterAgentOptions = (options) => options;

export const agentOptionLabel = (opt) => {
    if (opt == null) return '';
    if (typeof opt !== 'object') return `Agent ID:${opt}`;
    return opt.display_name || opt.email || `Agent ID:${opt.id ?? ''}`;
};

const useStyles = makeStyles((theme) => ({
    toolbarRoot: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
        maxWidth: '90vw !important',
    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
    pillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '86px',
        height: '32px',
        padding: '0px 16px',
        borderRadius: '20px',
        gap: '4px',
        color: 'black',
        backgroundColor: '#D8D8D8',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            width: '90px',
            height: '28px',
            padding: '0px 10px',
            borderRadius: '18px',
        },
    },
    selectedPillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '86px',
        height: '32px',
        padding: '0px 16px',
        borderRadius: '20px',
        gap: '4px',
        backgroundColor: '#007AFF',
        color: 'white',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            width: '90px',
            height: '28px',
            padding: '0px 10px',
            borderRadius: '18px',
        },
    },
}));

function AgentMetricsRangePickerActionBar({ metricsTimezone, onMetricsTimezoneChange, ...pickerActionBarProps }) {
    const z = metricsTimezone || 'UTC';
    // `className` includes MuiPickersLayout-actionBar — must be on the slot root so the layout grid
    // places this block full-width below the calendars (not on a nested DialogActions only).
    const { className, sx: layoutSx, ...pickersActionBarOnlyProps } = pickerActionBarProps;
    return (
        <Box
            className={className}
            sx={[
                {
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    borderTop: 1,
                    borderColor: 'divider',
                },
                ...(Array.isArray(layoutSx) ? layoutSx : [layoutSx].filter(Boolean)),
            ]}
        >
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Tooltip
                    arrow
                    title={`Calendar day range uses: ${z}. Event times in reports are stored in UTC.`}
                    placement="top-start"
                >
                    <Box sx={{ width: '100%', minWidth: { xs: 0, sm: 260 }, maxWidth: '100%' }}>
                        <TimezonePicker
                            label="Timezone"
                            variant="outlined"
                            size="small"
                            fullWidth
                            includeUtc
                            showTimezoneOffset
                            value={z}
                            onChange={(_ev, option) =>
                                onMetricsTimezoneChange(option?.value || 'UTC')}
                            componentsProps={{
                                paper: { sx: { maxWidth: 'min(560px, 94vw)' } },
                            }}
                        />
                    </Box>
                </Tooltip>
            </Box>
            <PickersActionBar {...pickersActionBarOnlyProps} />
        </Box>
    );
}

export const AgentMetricsListToolbar = (props) => {
    const classes = useStyles();
    const {
        recordListFilters,
        setRecordListFilters,
        metricsTimezone,
        onMetricsTimezoneChange,
        byDayAllowed,
        byDay,
        byDaySupported,
    } = props;
    const isSmallScreen = !useIsWidthUp('sm');
    // CP-8224: visibility level. At "own" the backend only ever returns the
    // requester's row, so the Agent / Access Group filters are no-ops; at
    // "guides" the report is own row + guides, so "Exclude guides" is pointless.
    const { hasPerm } = React.useContext(ContextStore);
    const hasAllMetricsPerm = hasPerm('agent_metrics', 'all');
    const ownOnlyMetrics = !hasAllMetricsPerm && !hasPerm('agent_metrics', 'guides');
    const [remoteAgentValue, setRemoteAgentValue] = React.useState(null);
    const [remoteTripValue, setRemoteTripValue] = React.useState(null);
    const [remoteTagValue, setRemoteTagValue] = React.useState(null);
    const [remoteAccessGroupValue, setRemoteAccessGroupValue] = React.useState(null);
    const [excludeGuides, setExcludeGuides] = React.useState(false);
    const dataAPI = React.useContext(DataAPIContext);
    const agentDropdownRef = React.useRef(null);
    const tourDropdownRef = React.useRef(null);
    const tagsDropdownRef = React.useRef(null);
    const accessGroupDropdownRef = React.useRef(null);
    const headerRef = React.useRef(null);

    const [inputValue] = React.useState(props.query ? props.query : '');
    // Starts at the page's query, so the mount-time report below changes nothing: starting at
    // '' cleared a query from the URL and brought it back a second later, a fetch each time.
    const [debouncedValue, setDebouncedValue] = React.useState(inputValue);

    React.useEffect(() => {
        props.setQuery(debouncedValue);
    }, [debouncedValue]);

    React.useEffect(() => {
        const isDeletion = inputValue.length < debouncedValue.length;

        const timerId = setTimeout(() => {
            setDebouncedValue(inputValue);
        }, isDeletion ? 0 : 1000); // If deleting, apply immediately; else wait for debounce

        return () => {
            clearTimeout(timerId);
        };
    }, [inputValue]);



    const handleAgentFilterSelect = (ev, agent_data) => {

        let agent_ids = agent_data.map((a) => a.id ? a.id : a);
        if (agent_ids.length === 0) {
            agent_ids = null;
        }
        setRecordListFilters((prev) => {
            let agent_field = prev.filter((f) => f.field === 'agent_ids')[0];
            return [...prev.filter((f) => f.field !== 'agent_ids'), { ...agent_field, value: agent_ids, op: "isAnyOf" }];
        });
        setRemoteAgentValue(agent_data);
    };

    const handleExcludeGuidesChange = (val) => {
        setRecordListFilters((prev) => {
            let exclude_guides_field = prev.filter((f) => f.field === 'exclude_guides')[0];
            return [...prev.filter((f) => f.field !== 'exclude_guides'), {...exclude_guides_field, value: val.target.checked, op: "is" }];
        });
        setExcludeGuides(val.target.checked);
    }

    // One row per agent per day. Kept in the filters (and so the URL); the list only
    // sends it while the date range allows it (see AgentMetricsList).
    const handleByDayChange = (ev) => {
        const checked = ev.target.checked;
        setRecordListFilters((prev) => {
            const by_day_field = prev.filter((f) => f.field === 'group_by_day')[0] || { field: 'group_by_day' };
            return [...prev.filter((f) => f.field !== 'group_by_day'), { ...by_day_field, value: checked || null, op: "is" }];
        });
    };

    const handleTourFilterSelect = (ev, tour_data) => {
        let tour_ids = tour_data.map((t) => t.id ? t.id : t);
        if (tour_ids.length === 0) {
            tour_ids = null;
        }
        setRecordListFilters((prev) => {
            let tour_field = prev.filter((f) => f.field === 'tour_ids')[0];
            return [...prev.filter((f) => f.field !== 'tour_ids'), { ...tour_field, value: tour_ids, op: "isAnyOf" }];
        });
        setRemoteTripValue(tour_data);
    };

    const handleTagFilterSelect = (ev, tag_data) => {
        let tour_tags = tag_data.map((t) => t.id ? t.id : t);
        if (tour_tags.length === 0) {
            tour_tags = null;
        }
        setRecordListFilters((prev) => {
            let tour_field = prev.filter((f) => f.field === 'tag_ids')[0];
            return [...prev.filter((f) => f.field !== 'tag_ids'), { ...tour_field, value: tour_tags, op: "isAnyOf" }];
        });
        setRemoteTagValue(tag_data);
    };

    const handleAccessGroupFilterSelect = (ev, access_group_data) => {
        let access_group_ids = access_group_data.map((g) => g.id ? g.id : g);
        if (access_group_ids.length === 0) {
            access_group_ids = null;
        }
        setRecordListFilters((prev) => {
            let ag_field = prev.filter((f) => f.field === 'access_group_ids')[0];
            return [...prev.filter((f) => f.field !== 'access_group_ids'), { ...ag_field, value: access_group_ids, op: "isAnyOf" }];
        });
        setRemoteAccessGroupValue(access_group_data);
    };

    const z = metricsTimezone || 'UTC';

    const AgentMetricsRangePickerActionBarSlot = React.useMemo(
        () =>
            function AgentMetricsRangePickerActionBarSlot(props) {
                return (
                    <AgentMetricsRangePickerActionBar
                        {...props}
                        metricsTimezone={metricsTimezone}
                        onMetricsTimezoneChange={onMetricsTimezoneChange}
                    />
                );
            },
        [metricsTimezone, onMetricsTimezoneChange]
    );

    // Preset day ranges (calendar dates in metricsTimezone): all time, 7, 30, 90, 365.
    const handleDateFilterSelect = (option) => {
        const todays_date = moment.tz(z).format('YYYY-MM-DD');
        let anchor = moment.tz(z);
        let date_filter;

        if (option === 'all time') {
            setRecordListFilters((prev) => {
                let tour_field_start = prev.filter((f) => f.field === 'start_datetime')[0];
                let tour_field_end = prev.filter((f) => f.field === 'end_datetime')[0];
                return [...prev.filter((f) => f.field !== 'start_datetime' && f.field !== 'end_datetime'), { ...tour_field_start, value: null }, { ...tour_field_end, value: null }];
            });
            return;
        }

        if (option === 'last 7') {
            anchor = anchor.subtract(7, 'days');
        } else if (option === 'last 30') {
            anchor = anchor.subtract(30, 'days');
        } else if (option === 'last 90') {
            anchor = anchor.subtract(90, 'days');
        } else if (option === 'last 365') {
            anchor = anchor.subtract(365, 'days');
        }

        date_filter = anchor.format('YYYY-MM-DD');

        setRecordListFilters((prev) => {
            let tour_field_start = prev.filter((f) => f.field === 'start_datetime')[0];
            let tour_field_end = prev.filter((f) => f.field === 'end_datetime')[0];
            return [...prev.filter((f) => f.field !== 'start_datetime' && f.field !== 'end_datetime'), { ...tour_field_start, value: date_filter }, { ...tour_field_end, value: todays_date }];
        });
    };

    const isThisDateFilterSelected = (option) => {
        const todays_date = moment.tz(z).format('YYYY-MM-DD');

        if (option === 'all time') {
            if (
                recordListFilters.filter((f) => f.field === 'start_datetime')[0]?.value === null &&
                (recordListFilters.filter((f) => f.field === 'end_datetime')[0]?.value === null ||
                    recordListFilters.filter((f) => f.field === 'end_datetime')[0]?.value === todays_date)
            ) {
                return true;
            }
        }

        let date_filter_string;
        if (option === 'last 365') {
            date_filter_string = moment.tz(z).subtract(365, 'days').format('YYYY-MM-DD');
        } else if (option === 'last 90') {
            date_filter_string = moment.tz(z).subtract(90, 'days').format('YYYY-MM-DD');
        } else if (option === 'last 30') {
            date_filter_string = moment.tz(z).subtract(30, 'days').format('YYYY-MM-DD');
        } else if (option === 'last 7') {
            date_filter_string = moment.tz(z).subtract(7, 'days').format('YYYY-MM-DD');
        } else {
            return false;
        }

        return (
            recordListFilters.filter((f) => f.field === 'start_datetime')[0]?.value === date_filter_string &&
            recordListFilters.filter((f) => f.field === 'end_datetime')[0]?.value === todays_date
        );
    };

    const handleDateRangeChange = (dateRange) => {
        let dateStart = dateRange[0];
        let dateEnd = dateRange[1];
        if (dateStart) {
            dateStart = moment(dateStart).tz(z).format('YYYY-MM-DD');
        }
        if (dateEnd) {
            dateEnd = moment(dateEnd).tz(z).format('YYYY-MM-DD');
        }

        setRecordListFilters((prev) => {
            let tour_field_start = prev.filter((f) => f.field === 'start_datetime')[0];
            let tour_field_end = prev.filter((f) => f.field === 'end_datetime')[0];
            return [...prev.filter((f) => f.field !== 'start_datetime' && f.field !== 'end_datetime'), { ...tour_field_start, value: dateStart }, { ...tour_field_end, value: dateEnd }];
        });
    };

    const IsoToMomentFormat = (isoDate) => {
        if (!isoDate) {
            return null;
        }
        return moment.tz(isoDate, 'YYYY-MM-DD', z).startOf('day');
    };

    const handleExportCSV = async () => {
        await props.onExportList();
    }

    // helper: fetch minimal fields by id list
    async function fetchByIds(dataAPI, resource, ids, fields = ['id', 'name', 'display_name']) {
        if (!ids || ids.length === 0) return [];
        // Your API shape may vary; the idea is an id__in filter that returns minimal fields
        const res = await dataAPI(GET_LIST, resource, {
            pagination: { page: 1, perPage: ids.length },
            sort: { field: 'id', order: 'ASC' },
            filter: { id__in: ids, _fields: fields.join(',') },
        });
        // optionally sort back to the input id order
        const order = new Map(ids.map((id, i) => [String(id), i]));
        return (res?.data || []).slice().sort((a, b) =>
            (order.get(String(a.id)) ?? 0) - (order.get(String(b.id)) ?? 0)
        );
    }

    const agentsHydratedOnceRef = React.useRef(false);
    // agent hydrate
    React.useEffect(() => {
        if (agentsHydratedOnceRef.current) return; // only run once on mount
        const ids = recordListFilters.find(f => f.field === 'agent_ids')?.value;
        // If we already have hydrated objects matching these ids, skip
        const currentIds = Array.isArray(remoteAgentValue) ? remoteAgentValue.map(a => a.id) : [];
        const same =
            Array.isArray(ids) &&
            ids.length === currentIds.length &&
            ids.every((id, i) => String(id) === String(currentIds[i]));

        if (!same && Array.isArray(ids) && ids.length > 0) {
            fetchByIds(dataAPI, 'user', ids, ['id', 'display_name']).then(setRemoteAgentValue);

        }

        if (!ids || ids.length === 0) setRemoteAgentValue([]); // clear on empty

        if (remoteAgentValue != null) {
            agentsHydratedOnceRef.current = true;
        }
    }, [recordListFilters]);

    const toursHydratedOnceRef = React.useRef(false);
    // tour hydrate
    React.useEffect(() => {
        if (toursHydratedOnceRef.current) return; // only run once on mount
        const ids = recordListFilters.find(f => f.field === 'tour_ids')?.value;
        const currentIds = Array.isArray(remoteTripValue) ? remoteTripValue.map(t => t.id) : [];
        const same =
            Array.isArray(ids) &&
            ids.length === currentIds.length &&
            ids.every((id, i) => String(id) === String(currentIds[i]));

        if (!same && Array.isArray(ids) && ids.length > 0) {
            fetchByIds(dataAPI, 'tour', ids, ['id', 'name', 'is_bespoke', 'start_date', 'end_date'])
                .then(setRemoteTripValue);
        }
        if (!ids || ids.length === 0) setRemoteTripValue([]);
        if (remoteTripValue != null) {
            toursHydratedOnceRef.current = true;
        }
    }, [recordListFilters]);

    const tagsHydratedOnceRef = React.useRef(false);
    // tag hydrate
    React.useEffect(() => {
        if (tagsHydratedOnceRef.current) return; // only run once on mount
        const ids = recordListFilters.find(f => f.field === 'tag_ids')?.value;
        const currentIds = Array.isArray(remoteTagValue) ? remoteTagValue.map(t => t.id) : [];
        const same =
            Array.isArray(ids) &&
            ids.length === currentIds.length &&
            ids.every((id, i) => String(id) === String(currentIds[i]));

        if (!same && Array.isArray(ids) && ids.length > 0) {
            fetchByIds(dataAPI, 'tour_tag', ids, ['id', 'name', 'category'])
                .then(tags => {
                    setRemoteTagValue(tags);
                });
        }
        if (!ids || ids.length === 0) setRemoteTagValue([]);
        if (remoteTagValue != null) {
            tagsHydratedOnceRef.current = true;
        }
    }, [recordListFilters]);


   return (
       <Toolbar disableGutters={true} className={classes.toolbarRoot} ref={headerRef}>
           <Grid container justifyContent="space-between" alignItems="center">
               <div style={{}}>
                   {
                   <AnalyticsExportButton
                       isSmallScreen={isSmallScreen}
                       onClick={handleExportCSV}
                       tooltip="Export list as CSV"
                       anchorStyle={isSmallScreen ? {
                           position: 'fixed',
                           right: 0,
                           top: 65,
                           zIndex: 1000,
                       } : {
                           position: 'fixed',
                           right: 10,
                           top: 10,
                           zIndex: 1000,
                       }}
                   />
                     }
               </div>
                <Grid item xs={12} sm={12} container spacing={2} alignItems={'center'}>
                    {/*
                    <Grid item xs={12} sm={2.5}>
                        <StyledFilledTextField
                            label="Search"
                            size="small"
                            onChange={(ev) => setInputValue(ev.target.value)}
                            value={search_value}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton aria-label="do search">
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    */}
                    <Grid item sx={{ flexGrow: 1, flexBasis: 0, minWidth: 150 }}>
                        {!ownOnlyMetrics &&
                        <RemoteAutocomplete
                            ref={agentDropdownRef}
                            filterSelected
                            apiFilters={[{ field: 'regular_users', op: 'eq', value: false }]}
                            InputProps={{ required: true }}
                            resource="user"
                            label="Agent"
                            multiple
                            variant="filled"
                            size="small"
                            onChange={handleAgentFilterSelect}
                            value={remoteAgentValue ? remoteAgentValue : recordListFilters.filter(rlf => rlf.field === 'agent_ids')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'agent_ids')[0]?.value : []}
                            getOptionLabel={agentOptionLabel}
                            filterOptions={filterAgentOptions}
                            renderOption={(liProps, option) => {
                                const name = agentOptionLabel(option);
                                const showEmail = option.email && option.email !== name;
                                return (
                                    <li {...liProps}>
                                        <div>
                                            <div>{name}</div>
                                            {showEmail && (
                                                <Typography variant="caption" color="textSecondary">
                                                    {option.email}
                                                </Typography>
                                            )}
                                        </div>
                                    </li>
                                );
                            }}
                       />
                       }
                       <div style={{ position: 'absolute', top: !isSmallScreen ? undefined : headerRef.current ? headerRef.current.getBoundingClientRect().bottom - 164 : undefined, fontSize: ".8rem", display: 'flex', flexWrap: 'wrap' }}>
                           {hasAllMetricsPerm &&
                           <FormControlLabel
                               label="Exclude guides from results"
                               control={
                                   <Checkbox
                                       checked={excludeGuides}
                                       onChange={handleExcludeGuidesChange}
                                   />
                               }
                           />
                           }
                           {byDaySupported &&
                           <Tooltip
                               title={byDayAllowed
                                   ? 'One row per agent per day'
                                   : `Pick a date range of ${BY_DAY_MAX_DAYS} days or less to see one row per day`}
                           >
                               {/* span: a Tooltip cannot listen on a disabled child */}
                               <span>
                                   <FormControlLabel
                                       label="By day"
                                       disabled={!byDayAllowed}
                                       control={
                                           <Checkbox
                                               checked={!!byDay}
                                               onChange={handleByDayChange}
                                           />
                                       }
                                   />
                               </span>
                           </Tooltip>
                           }
                       </div>
                       <div style={{
                           position: 'absolute',
                           right: !isSmallScreen ? 10 : null,
                           top: headerRef.current ? headerRef.current.getBoundingClientRect().bottom - 120 : undefined,
                           fontSize: ".8rem"
                       }}>

                           <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: isSmallScreen ? '4px' : '8px' }}>
                               <Box className={isThisDateFilterSelected('all time') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('all time')} style={{ width: isSmallScreen ? '90px' : '123px' }}>
                                   <Typography fontSize={isSmallScreen ? 12 : 16} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>All Time</Typography>
                               </Box>
                               <Box className={isThisDateFilterSelected('last 7') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last 7')} style={{ width: isSmallScreen ? '90px' : '123px' }}>
                                   <Typography fontSize={isSmallScreen ? 12 : 16} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Last 7 days</Typography>
                               </Box>
                               <Box className={isThisDateFilterSelected('last 365') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last 365')} style={{ width: isSmallScreen ? '90px' : '123px' }}>
                                   <Typography fontSize={isSmallScreen ? 12 : 16} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Last 365 days</Typography>
                               </Box>
                               <Box className={isThisDateFilterSelected('last 30') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last 30')} style={{ width: isSmallScreen ? '90px' : '123px' }}>
                                   <Typography fontSize={isSmallScreen ? 12 : 16} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Last 30 days</Typography>
                               </Box>
                               <Box className={isThisDateFilterSelected('last 90') ? classes.selectedPillButton : classes.pillButton} onClick={() => handleDateFilterSelect('last 90')} style={{ width: isSmallScreen ? '90px' : '123px' }}>
                                   <Typography fontSize={isSmallScreen ? 12 : 16} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Last 90 days</Typography>
                               </Box>
                           </div>
                       </div>
                   </Grid>
                   <Grid item sx={{ flexGrow: 0, flexShrink: 0, width: 300 }}>
                       <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateRangePicker
                                slots={{
                                    field: SingleInputDateRangeField,
                                    actionBar: AgentMetricsRangePickerActionBarSlot,
                                }}
                                name="allowedRange"
                                variant="filled"
                                onChange={handleDateRangeChange}
                                format="MMMM D, YYYY"
                                timezone={z}
                                autoOk
                                label={'Dates'}
                                value={[IsoToMomentFormat(recordListFilters.filter(rlf => rlf.field === 'start_datetime')[0]?.value), IsoToMomentFormat(recordListFilters.filter(rlf => rlf.field === 'end_datetime')[0]?.value)]}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'filled',
                                        fullWidth: true
                                    },
                                    actionBar: {
                                        actions: ['clear', 'accept'],
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                   <Grid item sx={{ flexGrow: 1.5, flexBasis: 0, minWidth: 180 }}>
                       <RemoteAutocomplete
                           ref={tourDropdownRef}
                           filterSelected
                           InputProps={{ required: true }}
                           resource="tour"
                           label="Tour"
                           multiple
                           variant="filled"
                           size="small"
                           apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                           onChange={handleTourFilterSelect}
                           value={remoteTripValue ? remoteTripValue : recordListFilters.filter(rlf => rlf.field === 'tour_ids')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'tour_ids')[0]?.value : []}
                           getOptionLabel={(opt) => formatTourOptionLabel(opt)}
                       />
                   </Grid>
                   <Grid item sx={{ flexGrow: 1, flexBasis: 0, minWidth: 150 }}>
                       <RemoteAutocomplete
                           ref={tagsDropdownRef}
                           filterSelected
                           InputProps={{ required: true }}
                           resource="tour_tag"
                           label="Tags"
                           multiple
                           variant="filled"
                           size="small"
                           onChange={handleTagFilterSelect}
                           value={remoteTagValue ? remoteTagValue : recordListFilters.filter(rlf => rlf.field === 'tag_ids')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'tag_ids')[0]?.value : []}
                           getOptionLabel={(opt) => {
                               if (!opt || !opt.name) {
                                   return "Tag ID:" + opt
                               }
                               return opt.category.name + ": " + opt.name;
                           }}
                       />
                   </Grid>
                   {!ownOnlyMetrics &&
                   <Grid item sx={{ flexGrow: 1, flexBasis: 0, minWidth: 150 }}>
                       <RemoteAutocomplete
                           ref={accessGroupDropdownRef}
                           filterSelected
                           InputProps={{ required: true }}
                           resource="access_group"
                           urlSuffix={null}
                           label="Access Group"
                           multiple
                           variant="filled"
                           size="small"
                           onChange={handleAccessGroupFilterSelect}
                           value={remoteAccessGroupValue ? remoteAccessGroupValue : recordListFilters.filter(rlf => rlf.field === 'access_group_ids')[0]?.value ? recordListFilters.filter(rlf => rlf.field === 'access_group_ids')[0]?.value : []}
                           getOptionLabel={(opt) => {
                               if (!opt || !opt.name) {
                                   return "Access Group ID:" + opt
                               }
                               return opt.name;
                           }}
                       />
                   </Grid>
                   }

               </Grid>

           </Grid>
       </Toolbar>
    );
};

export default AgentMetricsListToolbar;
